import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';
import { api } from '../../api.js';
import { NumStat } from './DeltaStat.js';
import { meshVerdict, verdictTone } from '../budget.js';

interface MeshDiffProps {
  depotPath: string;
  leftRev: number;
  rightRev: number;
}

interface GlbStats {
  meshCount: number;
  primitiveCount: number;
  vertexCount: number;
  triangleCount: number;
  materialCount: number;
  textureCount: number;
}

export function MeshDiff({ depotPath, leftRev, rightRev }: MeshDiffProps) {
  const leftRef = useRef<MVElement | null>(null);
  const rightRef = useRef<MVElement | null>(null);
  const syncing = useRef(false);

  useEffect(() => {
    import('@google/model-viewer').catch(() => {
      // Already imported by AssetPreview in most flows.
    });
  }, []);

  useEffect(() => {
    const l = leftRef.current;
    const r = rightRef.current;
    if (!l || !r) return;
    function sync(from: MVElement, to: MVElement): void {
      if (syncing.current) return;
      syncing.current = true;
      try {
        const orbit = from.getCameraOrbit?.();
        if (orbit) {
          to.cameraOrbit = `${orbit.theta}rad ${orbit.phi}rad ${orbit.radius}m`;
        }
        const fov = from.getFieldOfView?.();
        if (typeof fov === 'number') {
          to.fieldOfView = `${fov}deg`;
        }
      } finally {
        requestAnimationFrame(() => {
          syncing.current = false;
        });
      }
    }
    function onLeftChange(): void { sync(l!, r!); }
    function onRightChange(): void { sync(r!, l!); }
    l.addEventListener('camera-change', onLeftChange);
    r.addEventListener('camera-change', onRightChange);
    return () => {
      l.removeEventListener('camera-change', onLeftChange);
      r.removeEventListener('camera-change', onRightChange);
    };
  }, [leftRev, rightRev]);

  return (
    <div className="flex flex-col gap-2">
      <StatsStrip depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />
      <span className="text-xs text-muted-foreground">
        Orbit either viewer — the other follows.
      </span>
      <div className="grid grid-cols-2 gap-3">
        <ViewerPanel
          rev={leftRev}
          src={api.damPreviewUrl(depotPath, leftRev)}
          mvRef={leftRef}
        />
        <ViewerPanel
          rev={rightRev}
          src={api.damPreviewUrl(depotPath, rightRev)}
          mvRef={rightRef}
        />
      </div>
    </div>
  );
}

async function fetchMeta(depotPath: string, rev: number): Promise<Record<string, unknown> | null> {
  const res = await fetch(
    `/api/dam/meta?path=${encodeURIComponent(depotPath)}&rev=${rev}`,
    { credentials: 'include' },
  );
  if (res.status !== 200) return null;
  return (await res.json()) as Record<string, unknown>;
}

function statsOf(meta: Record<string, unknown> | null | undefined): GlbStats | null {
  if (!meta) return null;
  const s = meta.glbStats;
  if (typeof s !== 'object' || s === null) return null;
  const stats = s as Record<string, unknown>;
  return {
    meshCount: numOr(stats.meshCount, 0),
    primitiveCount: numOr(stats.primitiveCount, 0),
    vertexCount: numOr(stats.vertexCount, 0),
    triangleCount: numOr(stats.triangleCount, 0),
    materialCount: numOr(stats.materialCount, 0),
    textureCount: numOr(stats.textureCount, 0),
  };
}

function numOr(v: unknown, d: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : d;
}

function StatsStrip({
  depotPath,
  leftRev,
  rightRev,
}: {
  depotPath: string;
  leftRev: number;
  rightRev: number;
}) {
  const leftQ = useQuery({
    queryKey: ['dam.meta', depotPath, leftRev],
    queryFn: () => fetchMeta(depotPath, leftRev),
  });
  const rightQ = useQuery({
    queryKey: ['dam.meta', depotPath, rightRev],
    queryFn: () => fetchMeta(depotPath, rightRev),
  });
  const left = statsOf(leftQ.data);
  const right = statsOf(rightQ.data);
  if (!left && !right) {
    return (
      <Tooltip label="Stats backfill: run `node /app/dist/scripts/backfillGlbStats.js` inside dashboard-renderer to populate.">
        <span className="cursor-help text-xs text-muted-foreground">
          ⓘ Mesh stats unavailable for these revisions
        </span>
      </Tooltip>
    );
  }
  // Triangle budget flag on the current (right) rev.
  const curTris = right?.triangleCount ?? left?.triangleCount ?? null;
  const verdict = curTris !== null ? meshVerdict(curTris) : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <NumStat label="Verts" left={left?.vertexCount} right={right?.vertexCount} />
      <NumStat label="Tris" left={left?.triangleCount} right={right?.triangleCount} />
      {verdict && verdict !== 'ok' && (
        <Badge tone={verdictTone(verdict)}>{verdict === 'over' ? '⚠ over budget' : 'dense'}</Badge>
      )}
      <NumStat label="Materials" left={left?.materialCount} right={right?.materialCount} />
      <NumStat label="Textures" left={left?.textureCount} right={right?.textureCount} />
      <NumStat label="Meshes" left={left?.meshCount} right={right?.meshCount} />
    </div>
  );
}

function ViewerPanel({
  rev,
  src,
  mvRef,
}: {
  rev: number;
  src: string;
  mvRef: React.MutableRefObject<MVElement | null>;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-center text-xs text-muted-foreground">#{rev}</span>
      <div className="h-[60vh] w-full overflow-hidden rounded-md bg-card">
        <model-viewer
          ref={mvRef as unknown as React.RefObject<HTMLElement>}
          src={src}
          camera-controls
          environment-image="legacy"
          tone-mapping="khronos-pbr-neutral"
          shadow-intensity={0.6}
          style={{ width: '100%', height: '100%', background: 'var(--color-card)' }}
        />
      </div>
    </div>
  );
}

interface MVElement extends HTMLElement {
  cameraOrbit?: string;
  fieldOfView?: string;
  getCameraOrbit?: () => { theta: number; phi: number; radius: number };
  getFieldOfView?: () => number;
}
